import { formatUnits, ZeroAddress } from 'ethers'

import gasTankFeeTokens from '@ambire-common/consts/gasTankFeeTokens'
import { Dapp } from '@ambire-common/interfaces/dapp'
import { AccountOp } from '@ambire-common/libs/accountOp/accountOp'
import { AccountOpStatus } from '@ambire-common/libs/accountOp/types'
import { IrCall } from '@ambire-common/libs/humanizer/interfaces'
import { humanizeAccountOpWithZkxClearSigning } from '@ambire-common/libs/zkxClearSigning'
import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import i18n from '@common/config/localization/localization'
import formatDateTime from '@common/utils/formatDateTime'

import { DappInteraction, DisplayBalanceChange, SubmittedAccountOpLike } from './types'

export const MAX_VISIBLE_BALANCE_CHANGES = 3

export const formatBalanceChangeAmount = (change: DisplayBalanceChange) => {
  const formattedAmount = formatDecimals(
    parseFloat(
      formatUnits(
        change.balanceChange < 0n ? -change.balanceChange : change.balanceChange,
        change.decimals
      )
    ),
    'amount'
  )

  return `${change.balanceChange < 0n ? '-' : '+'}${formattedAmount}`
}

export const getFullBalanceChangeAmount = (change: DisplayBalanceChange) =>
  formatUnits(
    change.balanceChange < 0n ? -change.balanceChange : change.balanceChange,
    change.decimals
  )

export const getBalanceChangeTooltipId = (
  change: DisplayBalanceChange,
  submittedAccountOp: SubmittedAccountOpLike
) =>
  `balance-change-${submittedAccountOp.id}-${change.chainId}-${change.address}-${change.balanceChange.toString()}`

export const getFormattedSubmittedDate = (timestamp: number) => formatDateTime(timestamp)

export const getTruncatedTxnHash = (txnId?: string) => {
  if (!txnId) return ''
  if (txnId.length <= 12) return txnId

  return `${txnId.slice(0, 6)}...${txnId.slice(-4)}`
}

export const getTruncatedNetworkName = (name?: string) => {
  if (!name) return ''
  if (name.length <= 15) return name

  return `${name.slice(0, 15)}...`
}

export const getModalFinalStatus = (
  status?: AccountOpStatus,
  unresolvedAfterNonceSpent = false
) => {
  switch (status) {
    case AccountOpStatus.UnknownButPastNonce:
      // See the same branch in StatusBadge: a spent nonce with no readable receipt
      // is not the same thing as a confirmed replacement.
      return unresolvedAfterNonceSpent
        ? { label: i18n.t('Could not confirm the transaction'), appearance: 'warningText' as const }
        : { label: i18n.t('Replaced by fee (RBF)'), appearance: 'errorText' as const }
    case AccountOpStatus.BroadcastButStuck:
      return {
        label: i18n.t('The transaction could not be found'),
        appearance: 'errorText' as const
      }
    case AccountOpStatus.Rejected:
      return { label: i18n.t('Failed to send'), appearance: 'errorText' as const }
    case AccountOpStatus.PartiallyComplete:
      return { label: i18n.t('Partially completed'), appearance: 'warningText' as const }
    case AccountOpStatus.Failure:
      return { label: i18n.t('Failed'), appearance: 'errorText' as const }
    case AccountOpStatus.Success:
      return { label: i18n.t('Confirmed'), appearance: 'successText' as const }
    case AccountOpStatus.BroadcastedButNotConfirmed:
      return { label: i18n.t('The transaction is pending'), appearance: 'warningText' as const }
    default:
      return null
  }
}

export const getOrderedBalanceChanges = (submittedAccountOp: SubmittedAccountOpLike) => {
  const balanceChanges = submittedAccountOp.balanceChanges || []
  const positiveChanges = balanceChanges.filter((change) => change.balanceChange > 0n)
  const negativeChanges = balanceChanges.filter((change) => change.balanceChange < 0n)
  const nativeNegativeChanges = negativeChanges.filter(
    (change) => change.address.toLowerCase() === ZeroAddress.toLowerCase()
  )
  const nonNativeNegativeChanges = negativeChanges.filter(
    (change) => change.address.toLowerCase() !== ZeroAddress.toLowerCase()
  )

  return [...positiveChanges, ...nonNativeNegativeChanges, ...nativeNegativeChanges]
}

// `gasFeePayment.amount` is denominated in the fee token's own units, so the
// decimals have to come from the fee token — a hardcoded 6 (the stablecoin case)
// rendered an 18-decimal fee token such as WPLS as "-412,911,198,238,874.3".
// `gasTankFeeTokens` is the same list the relayer credits deposits for, so every
// token that can pay a Gas Tank fee is in it.
const getGasTankFeeTokenDecimals = (inToken: string, chainId: bigint): number => {
  const address = (inToken || '').toLowerCase()
  const match =
    gasTankFeeTokens.find(
      (token) => token.address.toLowerCase() === address && token.chainId === chainId
    ) || gasTankFeeTokens.find((token) => token.address.toLowerCase() === address)

  // Last resort: the ERC20 default, which is also what every non-stablecoin
  // entry on the list uses.
  return match?.decimals ?? 18
}

export const getSyntheticGasTankBalanceChange = (
  submittedAccountOp: SubmittedAccountOpLike
): DisplayBalanceChange | null => {
  const gasFeePayment = submittedAccountOp.gasFeePayment

  if (!gasFeePayment?.isGasTank || gasFeePayment.amount <= 0n) return null

  const feeTokenChainId = gasFeePayment.feeTokenChainId || submittedAccountOp.chainId

  return {
    symbol: 'Gas Tank',
    name: 'Gas Tank',
    decimals: getGasTankFeeTokenDecimals(gasFeePayment.inToken, feeTokenChainId),
    address: 'gas-tank',
    chainId: feeTokenChainId,
    priceIn: [],
    marketDataIn: [],
    flags: {
      onGasTank: true,
      rewardsType: null,
      canTopUpGasTank: false,
      isFeeToken: true
    },
    amount: 0n,
    amountBefore: 0n,
    amountAfter: 0n,
    balanceChange: -gasFeePayment.amount,
    iconType: 'gasTank'
  }
}

export const getSummaryBalanceChanges = (
  submittedAccountOp: SubmittedAccountOpLike
): DisplayBalanceChange[] => {
  const balanceChanges = getOrderedBalanceChanges(submittedAccountOp)
  const syntheticGasTankBalanceChange = getSyntheticGasTankBalanceChange(submittedAccountOp)

  return syntheticGasTankBalanceChange
    ? [...balanceChanges, syntheticGasTankBalanceChange]
    : balanceChanges
}

export const getVisibleSummaryBalanceChanges = (balanceChanges: DisplayBalanceChange[]) => {
  const gasTankBalanceChange = balanceChanges.find((change) => change.iconType === 'gasTank')

  if (!gasTankBalanceChange || balanceChanges.length <= MAX_VISIBLE_BALANCE_CHANGES) {
    return balanceChanges.slice(0, MAX_VISIBLE_BALANCE_CHANGES)
  }

  return [
    ...balanceChanges
      .filter((change) => change.iconType !== 'gasTank')
      .slice(0, MAX_VISIBLE_BALANCE_CHANGES - 1),
    gasTankBalanceChange
  ]
}

export const getHumanizedCalls = (submittedAccountOp: SubmittedAccountOpLike): IrCall[] => {
  const accountOp: AccountOp = {
    id: submittedAccountOp.id,
    accountAddr: submittedAccountOp.accountAddr,
    chainId: submittedAccountOp.chainId,
    signingKeyAddr: submittedAccountOp.signingKeyAddr ?? null,
    signingKeyType: submittedAccountOp.signingKeyType ?? null,
    nonce: submittedAccountOp.nonce ?? null,
    eoaNonce: submittedAccountOp.eoaNonce,
    calls: submittedAccountOp.calls,
    feeCall: submittedAccountOp.feeCall,
    activatorCall: submittedAccountOp.activatorCall,
    gasLimit: submittedAccountOp.gasLimit ?? null,
    signature: submittedAccountOp.signature ?? null,
    gasFeePayment: submittedAccountOp.gasFeePayment,
    txnId: submittedAccountOp.txnId,
    status: submittedAccountOp.status,
    asUserOperation: submittedAccountOp.asUserOperation,
    signers: submittedAccountOp.signers,
    signed: submittedAccountOp.signed,
    safeTx: submittedAccountOp.safeTx,
    meta: submittedAccountOp.meta,
    flags: submittedAccountOp.flags
  }

  // Same ZKX clear-signing humanization as the signing screen, so a past transaction reads the
  // way it did while it was being signed
  return humanizeAccountOpWithZkxClearSigning(accountOp).map((call, index) => ({
    ...call,
    id: call.id || String(index)
  }))
}

export const getPresentationalStatus = (
  submittedAccountOp: SubmittedAccountOpLike
): SubmittedAccountOpLike['status'] => {
  if (
    submittedAccountOp.identifiedBy.type !== 'MultipleTxns' ||
    submittedAccountOp.status === AccountOpStatus.BroadcastedButNotConfirmed
  )
    return submittedAccountOp.status

  const callWithoutATxId = submittedAccountOp.calls.find((call) => call.txnId === undefined)
  return !callWithoutATxId ? submittedAccountOp.status : AccountOpStatus.PartiallyComplete
}

export const getDappInteractions = (
  submittedAccountOp: SubmittedAccountOpLike
): DappInteraction[] => {
  const interactions: DappInteraction[] = []
  const seen = new Set<string>()
  const humanizedCalls = getHumanizedCalls(submittedAccountOp)
  const sendAddresses = Array.from(
    new Set(
      humanizedCalls.flatMap((call) => {
        if (call.fullVisualization?.[0]?.content !== 'Send') return []

        return call.fullVisualization.flatMap((item) =>
          item.type === 'address' && item.address ? [item.address] : []
        )
      })
    )
  )

  const addInteraction = (interaction: DappInteraction) => {
    if (seen.has(interaction.id)) return
    seen.add(interaction.id)
    interactions.push(interaction)
  }

  submittedAccountOp.calls.forEach((call) => {
    const dapp = call.dapp as Dapp | undefined
    if (!dapp?.name) return

    addInteraction({
      id: `dapp:${dapp.id || `${dapp.name}-${dapp.url || ''}`}`,
      name: dapp.name.charAt(0).toUpperCase() + dapp.name.slice(1).toLowerCase(), // capitalize
      iconUrl: dapp.icon
    })
  })

  const isSwap = !!submittedAccountOp.meta?.swapTxn
  if (isSwap) {
    addInteraction({
      id: 'fallback:swap',
      name: i18n.t('Swap/Bridge'),
      iconType: 'swap'
    })
  }

  const fuelsGasTank = humanizedCalls.some(
    (call) => call.fullVisualization?.[0]?.content === 'Fuel gas tank with'
  )
  if (fuelsGasTank) {
    addInteraction({
      id: 'fallback:gasTank',
      name: i18n.t('Fuel gas tank'),
      iconType: 'ambire'
    })
  }

  if (submittedAccountOp.meta && 'setDelegation' in submittedAccountOp.meta) {
    if (submittedAccountOp.meta.setDelegation === false) {
      addInteraction({
        id: 'fallback:revoke',
        name: i18n.t('Revoke delegation'),
        iconType: 'ambire'
      })
    } else {
      addInteraction({
        id: 'fallback:delegation',
        name: i18n.t('Enable smart settings'),
        iconType: 'ambire'
      })
    }
  }

  if (!interactions.length) {
    if (submittedAccountOp.activitySource === 'external') {
      addInteraction({
        id: 'fallback:receive',
        name: i18n.t('Receive'),
        iconType: 'receive'
      })
    } else {
      addInteraction({
        id: 'fallback:send',
        name: i18n.t('Send'),
        iconType: 'send',
        address: sendAddresses.length === 1 ? sendAddresses[0] : undefined,
        description: sendAddresses.length > 1 ? i18n.t('multiple addresses') : undefined
      })
    }
  }

  return interactions
}
